import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Faible', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', description: 'Désaccord mineur, résolution attendue sous 7 jours' },
  { value: 'medium', label: 'Modéré', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', description: 'Litige significatif, impact financier modéré' },
  { value: 'high', label: 'Élevé', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500', description: 'Litige grave, blocage de transaction, intervention urgente' },
  { value: 'critical', label: 'Critique', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', description: 'Fraude suspectée ou montant très élevé — escalade immédiate' },
];

const DISPUTE_CATEGORIES = [
  'Montant séquestre incorrect',
  'Retard de libération des fonds',
  'Documents manquants ou invalides',
  'Fraude suspectée',
  'Non-conformité contractuelle',
  'Problème de virement bancaire',
  'Désaccord sur les conditions de vente',
  'Autre',
];

const formatDate = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

const formatAmount = (n, currency = 'XOF') => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(n);
};

// ─── Timeline Item ────────────────────────────────────────────────────────────
const TimelineItem = ({ event, isLast }) => {
  const iconMap = {
    submitted: { icon: 'FileText', color: 'text-blue-600', bg: 'bg-blue-100' },
    routed: { icon: 'Send', color: 'text-purple-600', bg: 'bg-purple-100' },
    under_review: { icon: 'Eye', color: 'text-amber-600', bg: 'bg-amber-100' },
    resolved: { icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-100' },
    escalated: { icon: 'AlertTriangle', color: 'text-red-600', bg: 'bg-red-100' },
    evidence_added: { icon: 'Paperclip', color: 'text-gray-600', bg: 'bg-gray-100' },
  };
  const cfg = iconMap?.[event?.type] || { icon: 'Circle', color: 'text-gray-400', bg: 'bg-gray-100' };

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-9 h-9 rounded-full ${cfg?.bg} flex items-center justify-center flex-shrink-0 z-10`}>
          <Icon name={cfg?.icon} size={16} className={cfg?.color} />
        </div>
        {!isLast && <div className="w-0.5 bg-border/40 flex-1 mt-1" />}
      </div>
      <div className={`pb-6 flex-1 ${isLast ? '' : ''}`}>
        <p className="text-sm font-semibold text-text-primary">{event?.label}</p>
        {event?.description && <p className="text-xs text-text-secondary mt-0.5">{event?.description}</p>}
        <p className="text-xs text-text-secondary/60 mt-1">{formatDate(event?.timestamp)}</p>
      </div>
    </div>
  );
};

// ─── Severity Selector ────────────────────────────────────────────────────────
const SeveritySelector = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-3">
    {SEVERITY_LEVELS?.map(level => (
      <button
        key={level?.value}
        type="button"
        onClick={() => onChange(level?.value)}
        className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
          value === level?.value
            ? `${level?.border} ${level?.bg}`
            : 'border-border/40 bg-surface/30 hover:border-border/70'
        }`}
      >
        <div className={`w-3 h-3 rounded-full mt-0.5 flex-shrink-0 ${value === level?.value ? level?.dot : 'bg-gray-300'}`} />
        <div>
          <p className={`text-sm font-bold ${value === level?.value ? level?.color : 'text-text-primary'}`}>{level?.label}</p>
          <p className="text-xs text-text-secondary mt-0.5 leading-snug">{level?.description}</p>
        </div>
      </button>
    ))}
  </div>
);

// ─── Evidence Attachment ──────────────────────────────────────────────────────
const EvidenceAttachment = ({ files, onAdd, onRemove }) => {
  const handleFileChange = (e) => {
    const newFiles = Array.from(e?.target?.files || []);
    onAdd(newFiles);
    e.target.value = '';
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-border/50 rounded-xl cursor-pointer hover:border-brand-earth/50 hover:bg-brand-earth/5 transition-all">
        <Icon name="Upload" size={18} className="text-text-secondary" />
        <div>
          <p className="text-sm font-medium text-text-primary">Ajouter des pièces jointes</p>
          <p className="text-xs text-text-secondary">PDF, images, documents — max 10 Mo par fichier</p>
        </div>
        <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFileChange} className="hidden" />
      </label>

      {files?.length > 0 && (
        <div className="space-y-2">
          {files?.map((file, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 bg-surface/50 border border-border/40 rounded-lg">
              <Icon name="FileText" size={15} className="text-brand-earth flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-text-primary truncate">{file?.name}</p>
                <p className="text-[10px] text-text-secondary">{(file?.size / 1024)?.toFixed(1)} Ko</p>
              </div>
              <button onClick={() => onRemove(i)} className="text-text-secondary hover:text-red-500 transition-colors">
                <Icon name="X" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Escalation Card ──────────────────────────────────────────────────────────
const EscalationCard = ({ escalation }) => {
  const [expanded, setExpanded] = useState(false);
  const severityConfig = SEVERITY_LEVELS?.find(s => s?.value === escalation?.severity_level) || SEVERITY_LEVELS?.[0];

  const timeline = [
    { type: 'submitted', label: 'Escalade soumise', description: `Par ${escalation?.submitted_by_name || 'Utilisateur'}`, timestamp: escalation?.created_at },
    ...(escalation?.routed_at ? [{ type: 'routed', label: 'Routée vers l\'équipe finance', description: 'Routage automatique', timestamp: escalation?.routed_at }] : []),
    ...(escalation?.review_started_at ? [{ type: 'under_review', label: 'Examen en cours', description: escalation?.assigned_to_name || 'Équipe finance', timestamp: escalation?.review_started_at }] : []),
    ...(escalation?.resolved_at ? [{ type: 'resolved', label: 'Litige résolu', description: escalation?.resolution_summary, timestamp: escalation?.resolved_at }] : []),
  ];

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    routed: 'bg-purple-100 text-purple-700 border-purple-200',
    under_review: 'bg-blue-100 text-blue-700 border-blue-200',
    resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
  };

  return (
    <div className="bg-white dark:bg-card rounded-2xl border border-border/50 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl ${severityConfig?.bg} flex items-center justify-center flex-shrink-0`}>
              <Icon name="AlertTriangle" size={18} className={severityConfig?.color} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-text-primary">{escalation?.title || 'Escalade de litige'}</p>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${severityConfig?.bg} ${severityConfig?.color} ${severityConfig?.border}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${severityConfig?.dot}`} />
                  {severityConfig?.label}
                </span>
              </div>
              <p className="text-xs text-text-secondary mt-0.5">
                Réf: {escalation?.transaction_ref || '—'} · {escalation?.category || '—'}
              </p>
              <p className="text-xs text-text-secondary/60 mt-0.5">{formatDate(escalation?.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors?.[escalation?.status] || statusColors?.pending}`}>
              {escalation?.status === 'pending' ? 'En attente' : escalation?.status === 'routed' ? 'Routée' : escalation?.status === 'under_review' ? 'En examen' : escalation?.status === 'resolved' ? 'Résolue' : 'Rejetée'}
            </span>
            <button onClick={() => setExpanded(p => !p)} className="text-text-secondary hover:text-text-primary transition-colors">
              <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={18} />
            </button>
          </div>
        </div>

        {escalation?.description && (
          <p className="text-xs text-text-secondary mt-3 leading-relaxed line-clamp-2">{escalation?.description}</p>
        )}
      </div>

      {expanded && (
        <div className="border-t border-border/40 p-5 space-y-5">
          {/* Amount */}
          {escalation?.amount && (
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded-xl">
              <Icon name="DollarSign" size={16} className="text-brand-earth" />
              <div>
                <p className="text-xs text-text-secondary">Montant concerné</p>
                <p className="text-sm font-bold text-text-primary">{formatAmount(escalation?.amount, escalation?.currency)}</p>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <p className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-4">Chronologie</p>
            <div className="space-y-0">
              {timeline?.map((event, i) => (
                <TimelineItem key={i} event={event} isLast={i === timeline?.length - 1} />
              ))}
            </div>
          </div>

          {/* Evidence files */}
          {escalation?.evidence_files?.length > 0 && (
            <div>
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-3">Pièces jointes ({escalation?.evidence_files?.length})</p>
              <div className="space-y-2">
                {escalation?.evidence_files?.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-surface/50 border border-border/40 rounded-lg">
                    <Icon name="FileText" size={14} className="text-brand-earth" />
                    <span className="text-xs text-text-primary">{file?.name || `Pièce jointe ${i + 1}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolution */}
          {escalation?.resolution_summary && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-xs font-bold text-emerald-700 mb-1">Résolution</p>
              <p className="text-xs text-emerald-800 leading-relaxed">{escalation?.resolution_summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const EscrowDisputeEscalation = () => {
  const { user, userProfile } = useAuth();
  const [activeView, setActiveView] = useState('list');
  const [escalations, setEscalations] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  const [form, setForm] = useState({
    title: '',
    transaction_ref: '',
    category: '',
    severity_level: 'medium',
    description: '',
    amount: '',
    currency: 'XOF',
    evidence_files: [],
    timeline_notes: '',
  });
  const [formError, setFormError] = useState(null);

  const userRole = userProfile?.role || 'agent';
  const canEscalate = ['agent', 'notary', 'admin', 'super_admin']?.includes(userRole);

  const loadEscalations = useCallback(async () => {
    setLoadingList(true);
    try {
      let query = supabase
        ?.from('escrow_dispute_escalations')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (!['admin', 'super_admin']?.includes(userRole)) {
        query = query?.eq('submitted_by', user?.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setEscalations(data || []);
    } catch (err) {
      console.error('Error loading escalations:', err);
    } finally {
      setLoadingList(false);
    }
  }, [user?.id, userRole]);

  React.useEffect(() => {
    loadEscalations();
  }, [loadEscalations]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setFormError('Le titre est requis.'); return; }
    if (!form?.category) { setFormError('Veuillez sélectionner une catégorie.'); return; }
    if (!form?.description?.trim()) { setFormError('La description est requise.'); return; }

    setSubmitting(true);
    setFormError(null);

    try {
      const now = new Date()?.toISOString();
      const evidenceFilesMeta = form?.evidence_files?.map(f => ({ name: f?.name, size: f?.size, type: f?.type }));

      const { error: insertError } = await supabase
        ?.from('escrow_dispute_escalations')
        ?.insert({
          title: form?.title?.trim(),
          transaction_ref: form?.transaction_ref?.trim() || null,
          category: form?.category,
          severity_level: form?.severity_level,
          description: form?.description?.trim(),
          amount: form?.amount ? parseFloat(form?.amount) : null,
          currency: form?.currency,
          evidence_files: evidenceFilesMeta,
          timeline_notes: form?.timeline_notes?.trim() || null,
          submitted_by: user?.id,
          submitted_by_name: userProfile?.full_name || user?.email,
          submitted_by_role: userRole,
          status: 'pending',
          routed_at: now,
          routed_to: 'finance_team',
          created_at: now,
        });

      if (insertError) throw insertError;

      // Log to escrow_action_log
      await supabase?.from('escrow_action_log')?.insert({
        action_type: 'dispute_escalation_submitted',
        performed_by: user?.id,
        notes: `Escalade soumise: ${form?.title} — Sévérité: ${form?.severity_level} — Catégorie: ${form?.category}`,
      })?.catch(() => {});

      setSubmitSuccess(true);
      setForm({ title: '', transaction_ref: '', category: '', severity_level: 'medium', description: '', amount: '', currency: 'XOF', evidence_files: [], timeline_notes: '' });
      await loadEscalations();
      setTimeout(() => { setSubmitSuccess(false); setActiveView('list'); }, 2500);
    } catch (err) {
      setFormError(`Erreur lors de la soumission : ${err?.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredEscalations = escalations?.filter(e => {
    if (filterStatus !== 'all' && e?.status !== filterStatus) return false;
    if (filterSeverity !== 'all' && e?.severity_level !== filterSeverity) return false;
    return true;
  });

  const stats = {
    total: escalations?.length,
    pending: escalations?.filter(e => e?.status === 'pending')?.length,
    critical: escalations?.filter(e => e?.severity_level === 'critical')?.length,
    resolved: escalations?.filter(e => e?.status === 'resolved')?.length,
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Icon name="AlertOctagon" size={20} className="text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-text-primary">Escalade de Litiges Séquestre</h1>
            </div>
            <p className="text-sm text-text-secondary ml-13">
              Escaladez formellement un litige avec pièces jointes, niveau de sévérité et routage automatique vers l'équipe finance.
            </p>
          </div>
          {canEscalate && (
            <button
              onClick={() => setActiveView(activeView === 'form' ? 'list' : 'form')}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm"
            >
              <Icon name={activeView === 'form' ? 'List' : 'Plus'} size={16} />
              {activeView === 'form' ? 'Voir les escalades' : 'Nouvelle escalade'}
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: stats?.total, icon: 'FileText', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'En attente', value: stats?.pending, icon: 'Clock', color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Critiques', value: stats?.critical, icon: 'AlertTriangle', color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Résolues', value: stats?.resolved, icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          ]?.map((s, i) => (
            <div key={i} className="bg-white dark:bg-card rounded-xl border border-border/50 p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${s?.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon name={s?.icon} size={18} className={s?.color} />
              </div>
              <div>
                <p className="text-xl font-bold text-text-primary">{s?.value}</p>
                <p className="text-xs text-text-secondary">{s?.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Form View */}
        {activeView === 'form' && (
          <div className="bg-white dark:bg-card rounded-2xl border border-border/50 overflow-hidden">
            <div className="px-6 py-5 border-b border-border/40">
              <h2 className="text-base font-bold text-text-primary flex items-center gap-2">
                <Icon name="FilePlus" size={18} className="text-red-500" />
                Soumettre une escalade formelle
              </h2>
              <p className="text-xs text-text-secondary mt-1">Cette escalade sera automatiquement routée vers l'équipe finance.</p>
            </div>

            {submitSuccess ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Icon name="CheckCircle" size={36} className="text-emerald-500" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-emerald-700">Escalade soumise !</p>
                  <p className="text-sm text-text-secondary mt-1">Routée automatiquement vers l'équipe finance.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-6">

                {/* Title + Ref */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1.5">Titre de l'escalade *</label>
                    <input
                      type="text"
                      value={form?.title}
                      onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))}
                      placeholder="Ex: Retard libération fonds — Transaction #XYZ"
                      className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1.5">Référence transaction</label>
                    <input
                      type="text"
                      value={form?.transaction_ref}
                      onChange={e => setForm(p => ({ ...p, transaction_ref: e?.target?.value }))}
                      placeholder="Ex: TXN-2026-001234"
                      className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1.5">Catégorie du litige *</label>
                  <select
                    value={form?.category}
                    onChange={e => setForm(p => ({ ...p, category: e?.target?.value }))}
                    className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-red-400 transition-colors"
                  >
                    <option value="">Sélectionner une catégorie…</option>
                    {DISPUTE_CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Severity */}
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-3">Niveau de sévérité *</label>
                  <SeveritySelector value={form?.severity_level} onChange={v => setForm(p => ({ ...p, severity_level: v }))} />
                </div>

                {/* Amount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1.5">Montant concerné</label>
                    <input
                      type="number"
                      value={form?.amount}
                      onChange={e => setForm(p => ({ ...p, amount: e?.target?.value }))}
                      placeholder="0"
                      min="0"
                      className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-1.5">Devise</label>
                    <select
                      value={form?.currency}
                      onChange={e => setForm(p => ({ ...p, currency: e?.target?.value }))}
                      className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-red-400 transition-colors"
                    >
                      <option value="XOF">XOF (FCFA)</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1.5">Description détaillée *</label>
                  <textarea
                    value={form?.description}
                    onChange={e => setForm(p => ({ ...p, description: e?.target?.value }))}
                    rows={4}
                    placeholder="Décrivez le litige en détail : contexte, parties impliquées, chronologie des événements, impact financier…"
                    className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-red-400 transition-colors resize-none"
                  />
                </div>

                {/* Timeline notes */}
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1.5">Notes de chronologie</label>
                  <textarea
                    value={form?.timeline_notes}
                    onChange={e => setForm(p => ({ ...p, timeline_notes: e?.target?.value }))}
                    rows={2}
                    placeholder="Dates clés, événements importants, tentatives de résolution précédentes…"
                    className="w-full px-3 py-2.5 rounded-xl border border-border/60 bg-surface/50 text-sm text-text-primary focus:outline-none focus:border-red-400 transition-colors resize-none"
                  />
                </div>

                {/* Evidence */}
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-2">Pièces justificatives</label>
                  <EvidenceAttachment
                    files={form?.evidence_files}
                    onAdd={newFiles => setForm(p => ({ ...p, evidence_files: [...p?.evidence_files, ...newFiles] }))}
                    onRemove={i => setForm(p => ({ ...p, evidence_files: p?.evidence_files?.filter((_, idx) => idx !== i) }))}
                  />
                </div>

                {/* Routing notice */}
                <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                  <Icon name="Send" size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-purple-800">Routage automatique vers l'équipe finance</p>
                    <p className="text-xs text-purple-700 mt-0.5 leading-relaxed">
                      Cette escalade sera immédiatement routée vers l'équipe finance pour examen.
                      {form?.severity_level === 'critical' && ' ⚠️ Niveau critique — traitement prioritaire sous 2h.'}
                      {form?.severity_level === 'high' && ' Niveau élevé — traitement sous 24h.'}
                    </p>
                  </div>
                </div>

                {formError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <Icon name="AlertCircle" size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{formError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setActiveView('list')}
                    className="flex-1 px-4 py-3 border border-border/60 text-text-secondary rounded-xl text-sm font-medium hover:bg-surface/50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Icon name="Loader2" size={15} className="animate-spin" />Soumission…</>
                    ) : (
                      <><Icon name="Send" size={15} />Soumettre l'escalade</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* List View */}
        {activeView === 'list' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Icon name="Filter" size={14} className="text-text-secondary" />
                <span className="text-xs font-semibold text-text-secondary">Filtrer :</span>
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e?.target?.value)}
                className="px-3 py-1.5 rounded-lg border border-border/60 bg-surface/50 text-xs text-text-primary focus:outline-none"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="routed">Routées</option>
                <option value="under_review">En examen</option>
                <option value="resolved">Résolues</option>
              </select>
              <select
                value={filterSeverity}
                onChange={e => setFilterSeverity(e?.target?.value)}
                className="px-3 py-1.5 rounded-lg border border-border/60 bg-surface/50 text-xs text-text-primary focus:outline-none"
              >
                <option value="all">Toutes sévérités</option>
                {SEVERITY_LEVELS?.map(s => <option key={s?.value} value={s?.value}>{s?.label}</option>)}
              </select>
              <button onClick={loadEscalations} className="flex items-center gap-1.5 px-3 py-1.5 border border-border/60 rounded-lg text-xs text-text-secondary hover:text-text-primary transition-colors">
                <Icon name="RefreshCw" size={12} />
                Actualiser
              </button>
            </div>

            {loadingList ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 rounded-full border-2 border-red-500 border-t-transparent animate-spin" />
              </div>
            ) : filteredEscalations?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white dark:bg-card rounded-2xl border border-border/50">
                <div className="w-14 h-14 rounded-full bg-surface/50 flex items-center justify-center">
                  <Icon name="AlertOctagon" size={28} className="text-text-secondary/40" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-text-primary">Aucune escalade trouvée</p>
                  <p className="text-xs text-text-secondary mt-1">
                    {canEscalate ? 'Cliquez sur "Nouvelle escalade" pour soumettre un litige.' : 'Aucune escalade disponible.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEscalations?.map(escalation => (
                  <EscalationCard key={escalation?.id} escalation={escalation} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EscrowDisputeEscalation;
